import React, { useState, useEffect } from "react";
import { Logo } from "components";
import useForm from "../../hooks/validation/useForm";
import { useNavigate, useLocation } from "react-router-dom";
import { validatePassword } from "../../components/utils/validationUtils";
import { useChangePassword } from "hooks/auth/useChangePassword";
import { useCheckResetToken } from 'hooks';
import { Flex, Link, Text, Button, TextField, Progress } from '@radix-ui/themes';
import * as Form from "@radix-ui/react-form";

export default function Changepassword() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [passwordChanged, setPasswordChanged] = useState(true);

  const queryParams = new URLSearchParams(search);
  const token = queryParams.get('token');
  const uidb64 = queryParams.get('uidb64');

  const { data: verifyData, status: verifyStatus, error: verifyError } = useCheckResetToken(token);


  const initialFormState = {
    password: "",
    confirmPassword: "",
    token: token,
    uidb64: uidb64
  };

  const {
    mutate: changePassword,
    isPending: isChangingPasswordPending,
  } = useChangePassword();

 
 
  const validations = {
    password: (value) =>
      !value || !validatePassword(value)
        ? "Your password must be at least 8 characters long and include at least one letter, one number, and one special character (e.g., !@#$%^&*_+-;':\"\\|,./?~)"
        : "",
    confirmPassword: (value, formState) =>
      !value
        ? "Input a confirm password"
        : value !== formState.password
        ? "Passwords do not match"
        : "",
  };

  const { formState, errors, handleChange, handleSubmit } = useForm(
    initialFormState,
    validations
  );
 
  const handlePasswordSubmit = async (formState) => {
    const new_password = {
      new_password: formState.password,
      token: formState.token,
      uidb64: formState.uidb64
    }
    changePassword(new_password)
    setPasswordChanged(false)
    
  };

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  
  useEffect(() => {
    const img = new Image();
    img.src = '/LoginPageBG.png';
    img.onload = () => {
      setIsImageLoaded(true);
    };
  }, []);

  // While verifying a provided token, show a full-width progress bar and render nothing else
  if (token && (verifyStatus === 'idle' || verifyStatus === 'loading')) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 1000 }}>
        <Progress size="1" duration="1" />
      </div>
    );
  }

  return (
    <Flex style={{ height: '100vh', width: '100vw'}}>
      {/* Change password section */}
      <Flex direction="column" align="stretch" px="7" py="8" style={{alignItems: 'center', flex: '1 1 50%'}}>
        <Flex justify="center" align="stretch" width="100%" style={{alignItems: 'center', flex: '1 0 0'}}>
          <Flex direction="column" gap="7" maxWidth="296px" style={{alignItems: 'center', justifyContent: 'center',
            width: '100%'}}>
            {passwordChanged ? (
              (() => {
                if (!token) {
                  return (
                    <>
                      <Text size="4" weight="bold" align="center">Reset Your Password</Text>
                      <Flex direction="column" gap="4">
                        <Text size="3" weight="medium" align="center">Password reset link is not active</Text>
                        <Text size="2" align="center">Token not provided. Please request a new password reset link.</Text>
                        <Button variant="solid" onClick={() => navigate('/forgot-password')}>Request a new link</Button>
                      </Flex>
                    </>
                  );
                }

                // Treat 'idle' and 'loading' the same to avoid flashing the form
                if (verifyStatus === 'idle' || verifyStatus === 'loading') {
                  return null;
                }

                if (verifyStatus === 'error') {
                  const code = verifyError?.response?.status;
                  const serverMsg = verifyError?.response?.data?.error || '';
                  // Expired
                  if (code === 400 && serverMsg.toLowerCase().includes('expired')) {
                    return (
                      <>
                        <Text size="4" weight="bold" align="center">Reset Your Password</Text>
                        <Flex direction="column" gap="4">
                          <Text size="3" weight="medium" align="center">Password reset link expired</Text>
                          <Text size="2" align="center">This password reset link has expired. Please request a new one to continue.</Text>
                          <Button variant="solid" onClick={() => navigate('/forgot-password')}>Request a new link</Button>
                        </Flex>
                      </>
                    );
                  }

                  // Not found
                  if (code === 404) {
                    return (
                      <>
                        <Text size="4" weight="bold" align="center">Reset Your Password</Text>
                        <Flex direction="column" gap="4">
                          <Text size="3" weight="medium" align="center">Password reset link invalid</Text>
                          <Text size="2" align="center">This password reset link is invalid or has already been used. Request a new link to reset your password.</Text>
                          <Button variant="solid" onClick={() => navigate('/forgot-password')}>Request a new link</Button>
                        </Flex>
                      </>
                    );
                  }

                  // Generic failure
                  return (
                    <>
                      <Text size="4" weight="bold" align="center">Reset Your Password</Text>
                      <Flex direction="column" gap="4">
                        <Text size="3" weight="medium" align="center">Password reset link failed</Text>
                        <Text size="2" align="center">{serverMsg || 'An error occurred while verifying the password reset link. Please request a new password reset link.'}</Text>
                        <Button variant="solid" onClick={() => navigate('/forgot-password')}>Request a new link</Button>
                      </Flex>
                    </>
                  );
                }

                // Only show the form when verification succeeded
                if (verifyStatus !== 'success') {
                  // safety fallback: render nothing (full-page progress handles loading)
                  return null;
                }

                // Success: show the form
                return (
                  <>
                    <Text size="4" weight="bold" align="center">Reset Your Password</Text>
                    <Form.Root onSubmit={(e) => handleSubmit(e, handlePasswordSubmit)} style={{width: '100%'}}>
                      <Flex direction="column" gap="5" style={{alignItems: 'flex-start', alignSelf: 'stretch'}}>
                        <Flex direction="column" gap="4" style={{alignItems: 'flex-start', alignSelf: 'stretch'}}>
                          {/* New password field */}
                          <Form.Field name="new password" style={{width: '100%'}}>
                            <Form.Label style={{display: 'flex', flexDirection: 'column', marginBottom: '4px'}}>
                              <Text size="2" weight="medium">New password</Text>
                            </Form.Label>
                            <Form.Control asChild>
                              <TextField.Root
                                name="password"
                                type="password"
                                size="2"
                                value={formState.password}
                                onChange={handleChange}
                              />
                            </Form.Control>
                            <Form.Message>
                              <Text size="1" weight="regular" color={errors.password ? "red" : "gray"}>
                                Your password must be at least 8 characters long and
                                include at least one letter, one number, and one special
                                character (e.g., !@#$%^&*_+-;':"\|,./?~)
                              </Text>
                            </Form.Message>
                          </Form.Field>
                          {/* Confirm password field */}
                          <Form.Field style={{width: '100%'}}>
                            <Form.Label style={{display: 'flex', flexDirection: 'column', marginBottom: '4px'}}>
                              <Text size="2" weight="medium">Confirm password</Text>
                            </Form.Label>
                            <Form.Control asChild>
                              <TextField.Root
                                name="confirmPassword"
                                type="password"
                                size="2"
                                value={formState.confirmPassword}
                                onChange={handleChange}
                              />
                            </Form.Control>
                            {errors.confirmPassword && (
                            <Form.Message>
                              <Text size="1" weight="regular" color="red">{errors.confirmPassword}</Text>
                            </Form.Message>
                            )}
                          </Form.Field>
                        </Flex>
                        <Button type="submit" variant="solid" radius="medium"
                          loading={isChangingPasswordPending}
                          disabled={ !formState.password || !formState.confirmPassword }
                          style={{alignSelf: 'stretch'}}>
                          Reset password
                        </Button>
                      </Flex>
                    </Form.Root>
                  </>
                );
              })()
            ) : (
              <Flex direction="column" gap="4">
                <Text size="3" weight="medium" align="center">Your password has been reset successfully!</Text>
                <Button 
                  variant="solid" 
                  radius="medium"
                  style={{alignSelf: 'stretch'}}
                  onClick={() => navigate('/login')}
                >
                  Login into b connect
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
        {/* Footer links */}
        <Flex gap="7" justify="center" width="100%">
          <Link size="1" weight="regular" color="gray" href="https://b-connect.co.uk/terms" target='_blank'>
            Terms of Service
          </Link>
          <Link size="1" weight="regular" color="gray" href="https://b-connect.co.uk/privacy" target='_blank'>
            Privacy Notice
          </Link>
          <Link size="1" weight="regular" color="gray" href="mailto:support@b-connect.co.uk">Support</Link>
        </Flex>
      </Flex>
      {/* Background Image and  Logo Section */}
      <Flex direction="column" align="stretch" style={{ padding: '80px 120px 0 120px', flex: '1 1 50%',
        background: isImageLoaded
        ? 'url(/LoginPageBG.png) no-repeat center center / cover'
        : 'var(--color-background)',
        transition: 'background 0.3s ease-in-out',
        backgroundBlendMode: 'soft-light', alignItems: 'center'}}>
        <Logo icon="LogoBWifi" width="40px" height="32px" color="white"/>
      </Flex>
    </Flex>
  );
}
